import { Link } from 'react-router-dom';

export default function DisabledWomenPost() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold text-purple-600">
              Kadın Hareketi
            </Link>
            <Link
              to="/blog"
              className="text-gray-600 hover:text-purple-600 transition-colors"
            >
              ← Back to Blog
            </Link>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
              Disability Rights
            </span>
            <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
              Women's Rights
            </span>
            <span>January 2, 2025</span>
            <span>15 min read</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Being a Disabled Woman in Turkey and Luxembourg: A Comparative Analysis of Rights, Challenges, and Resilience
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Disabled women face unique challenges at the intersection of gender and disability discrimination. This article explores the lived experiences, legal frameworks, and systemic barriers in Turkey and Luxembourg—and the strength of women who navigate both.
          </p>
        </header>

        {/* Featured Image */}
        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/564881e130f663a37d708c84edc6c197.jpg"
            alt="Disabled women showing strength and solidarity"
            className="w-full h-96 object-cover object-top"
          />
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg mb-8">
            <p className="text-lg text-gray-800 italic mb-0">
              "Being a disabled woman means facing double discrimination—for being a woman and for being disabled. But it also means double strength, double resilience, and double determination to create change."
            </p>
          </div>

          <p className="text-gray-700 leading-relaxed mb-6">
            Ayşe, 32, uses a wheelchair in Istanbul. She has a university degree in engineering but has been rejected from 47 job applications. When she asks why, employers cite "accessibility concerns" or say the position has been filled. She knows the real reason: they don't want to hire a disabled woman.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Marie, 28, is deaf and lives in Luxembourg. She has a job, accessible housing, and sign language interpreters provided by the state. But she still faces discrimination in healthcare, where doctors speak to her hearing companion instead of to her. She still experiences sexual harassment that goes unreported because she fears not being believed.
          </p>

          <p className="text-gray-700 leading-relaxed mb-8">
            These are just two stories among millions. Disabled women around the world face unique challenges at the intersection of gender and disability—what scholars call "double discrimination" or "intersectional oppression." This article explores what it means to be a disabled woman in two very different contexts: Turkey and Luxembourg.
          </p>

          {/* Section 1 */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            Understanding the Intersection: Gender + Disability
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Disabled women don't just face discrimination for being women. They don't just face discrimination for being disabled. They face a unique form of discrimination that combines and amplifies both.
          </p>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            The Statistics Tell a Stark Story
          </h3>

          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <p className="text-gray-800 font-semibold mb-4">Globally, disabled women are:</p>
            <ul className="space-y-2">
              <li className="text-gray-700">• <strong>2-3 times more likely</strong> to experience sexual violence than non-disabled women</li>
              <li className="text-gray-700">• <strong>2 times more likely</strong> to experience domestic violence</li>
              <li className="text-gray-700">• <strong>Less likely to be employed</strong> than disabled men or non-disabled women</li>
              <li className="text-gray-700">• <strong>More likely to live in poverty</strong> than any other demographic group</li>
              <li className="text-gray-700">• <strong>Less likely to marry</strong> and more likely to be divorced or abandoned</li>
              <li className="text-gray-700">• <strong>More likely to be sterilized</strong> without consent</li>
              <li className="text-gray-700">• <strong>Less likely to receive adequate healthcare</strong></li>
              <li className="text-gray-700">• <strong>More likely to be institutionalized</strong></li>
            </ul>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            Why Double Discrimination Happens
          </h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Stereotypes about women:</strong>
          </p>
          <ul className="space-y-2 mb-6">
            <li className="text-gray-700">• Women should be caregivers, not care receivers</li>
            <li className="text-gray-700">• Women's value is tied to physical appearance</li>
            <li className="text-gray-700">• Women should be mothers and wives</li>
            <li className="text-gray-700">• Women should be passive and dependent</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Stereotypes about disabled people:</strong>
          </p>
          <ul className="space-y-2 mb-6">
            <li className="text-gray-700">• Disabled people are asexual or undesirable</li>
            <li className="text-gray-700">• Disabled people are helpless and dependent</li>
            <li className="text-gray-700">• Disabled people can't work or contribute</li>
            <li className="text-gray-700">• Disabled people are burdens on society</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-8">
            <strong>When these stereotypes combine, disabled women are seen as:</strong> Undesirable, unemployable, incapable of motherhood, eternal children, burdens, invisible, and less than human.
          </p>

          {/* Section 2: Turkey */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            Being a Disabled Woman in Turkey
          </h2>

          <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
            <img
              src="https://readdy.ai/api/search-image?query=Turkish%20disabled%20woman%20in%20Istanbul%2C%20wheelchair%20user%2C%20urban%20setting%2C%20determination%2C%20strength%2C%20modern%20Turkey%2C%20beautiful%20woman%20showing%20resilience%2C%20city%20background%2C%20empowering&width=1200&height=500&seq=turkey-disabled-1&orientation=landscape"
              alt="Disabled woman in Turkey"
              className="w-full h-80 object-cover object-top"
            />
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            Legal Framework
          </h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Turkey has ratified:</strong>
          </p>
          <ul className="space-y-2 mb-6">
            <li className="text-gray-700">• UN Convention on the Rights of Persons with Disabilities (CRPD) - 2009</li>
            <li className="text-gray-700">• Convention on the Elimination of All Forms of Discrimination Against Women (CEDAW) - 1985</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Domestic laws include:</strong>
          </p>
          <ul className="space-y-2 mb-6">
            <li className="text-gray-700">• Law on Disabled People (No. 5378) - 2005</li>
            <li className="text-gray-700">• Turkish Constitution guarantees equality</li>
            <li className="text-gray-700">• 3% employment quota for disabled people in public sector</li>
            <li className="text-gray-700">• Accessibility requirements for public buildings</li>
          </ul>

          <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg mb-8">
            <p className="text-gray-800 font-semibold mb-2">The Reality:</p>
            <p className="text-gray-700 mb-0">
              Laws exist on paper, but enforcement is weak. Disabled women in Turkey face significant barriers in every aspect of life.
            </p>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            The Challenges Disabled Women Face in Turkey
          </h3>

          <div className="space-y-6 mb-8">
            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">1. Physical Accessibility</h4>
              <p className="text-gray-700 mb-3">
                <strong>The problem:</strong> Most buildings, public transportation, and sidewalks are not accessible.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Only 30% of public buildings meet accessibility standards</li>
                <li>• Most metro stations lack elevators</li>
                <li>• Sidewalks are often blocked or broken</li>
                <li>• Accessible bathrooms are rare</li>
              </ul>
              <p className="text-gray-700 mt-3 italic">
                <strong>Impact on women:</strong> Disabled women are more likely to be homebound, isolated, and dependent on family members. This increases vulnerability to abuse and limits access to education, employment, and healthcare.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">2. Employment Discrimination</h4>
              <p className="text-gray-700 mb-3">
                <strong>The problem:</strong> Disabled women face extreme employment discrimination.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Only 15% of disabled women are employed (vs. 30% of disabled men)</li>
                <li>• Employers often refuse to hire disabled women</li>
                <li>• When hired, disabled women earn 40% less than non-disabled women</li>
                <li>• Many employers pay the fine instead of meeting the 3% quota</li>
              </ul>
              <p className="text-gray-700 mt-3 italic">
                <strong>Impact on women:</strong> Economic dependence on family or partners, increased vulnerability to abuse, inability to leave abusive situations, poverty.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">3. Education Barriers</h4>
              <p className="text-gray-700 mb-3">
                <strong>The problem:</strong> Disabled girls are less likely to attend school than disabled boys or non-disabled girls.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 40% of disabled girls never attend school</li>
                <li>• Schools often lack accessibility features</li>
                <li>• Teachers lack training in inclusive education</li>
                <li>• Families prioritize educating sons over disabled daughters</li>
              </ul>
              <p className="text-gray-700 mt-3 italic">
                <strong>Impact on women:</strong> Limited literacy, reduced employment opportunities, increased vulnerability, social isolation.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">4. Healthcare Discrimination</h4>
              <p className="text-gray-700 mb-3">
                <strong>The problem:</strong> Disabled women face significant barriers to healthcare.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Gynecological exam tables are not accessible</li>
                <li>• Doctors often speak to companions instead of patients</li>
                <li>• Reproductive healthcare is often denied</li>
                <li>• Mental health services are inadequate</li>
                <li>• Sign language interpreters are rarely available</li>
              </ul>
              <p className="text-gray-700 mt-3 italic">
                <strong>Impact on women:</strong> Untreated health conditions, reproductive health problems, inability to access contraception or prenatal care, increased maternal mortality.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">5. Violence and Abuse</h4>
              <p className="text-gray-700 mb-3">
                <strong>The problem:</strong> Disabled women experience extremely high rates of violence.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 80% of disabled women experience some form of violence</li>
                <li>• Sexual violence rates are 3 times higher than non-disabled women</li>
                <li>• Domestic violence shelters are often not accessible</li>
                <li>• Police and courts often don't believe disabled women</li>
                <li>• Abusers are rarely prosecuted</li>
              </ul>
              <p className="text-gray-700 mt-3 italic">
                <strong>Impact on women:</strong> Trauma, inability to escape abuse, lack of justice, continued victimization.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">6. Social Stigma and Isolation</h4>
              <p className="text-gray-700 mb-3">
                <strong>The problem:</strong> Disabled women face intense social stigma.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Seen as unmarriageable and undesirable</li>
                <li>• Often hidden by families out of shame</li>
                <li>• Excluded from social events and gatherings</li>
                <li>• Infantilized and treated as eternal children</li>
                <li>• Sexuality is denied or seen as inappropriate</li>
              </ul>
              <p className="text-gray-700 mt-3 italic">
                <strong>Impact on women:</strong> Loneliness, depression, lack of social support, inability to form relationships, internalized shame.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">7. Reproductive Rights Violations</h4>
              <p className="text-gray-700 mb-3">
                <strong>The problem:</strong> Disabled women's reproductive rights are often violated.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Forced sterilization still occurs</li>
                <li>• Contraception is often denied</li>
                <li>• Pregnant disabled women face pressure to abort</li>
                <li>• Disabled mothers face custody challenges</li>
                <li>• Prenatal care is inadequate</li>
              </ul>
              <p className="text-gray-700 mt-3 italic">
                <strong>Impact on women:</strong> Loss of bodily autonomy, trauma, inability to have children if desired, forced pregnancy if contraception is denied.
              </p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            Voices from Turkey: Real Stories
          </h3>

          <div className="space-y-6 mb-8">
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Zeynep, 35, visually impaired, Ankara:</p>
              <p className="text-gray-700 italic">
                "I have a master's degree in psychology. I applied to 60 jobs. Only 3 called me for interviews. When they saw I was blind, they said the position was filled. I finally got a job at a call center for minimum wage. My male colleague with the same disability and less education earns more than me."
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Elif, 28, wheelchair user, Istanbul:</p>
              <p className="text-gray-700 italic">
                "I was sexually assaulted by my caregiver. When I reported it to police, they asked if I was sure I understood what happened. They asked if maybe I misunderstood. They never investigated. He still works as a caregiver."
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Aylin, 42, deaf, Izmir:</p>
              <p className="text-gray-700 italic">
                "When I go to the doctor, they speak to my sister instead of me. They act like I'm not there. When I got pregnant, the doctor told my husband I shouldn't have children. He didn't ask me. He didn't speak to me. I had my daughter anyway. She's 10 now and she's my interpreter. A child shouldn't have to do that."
              </p>
            </div>
          </div>

          {/* Section 3: Luxembourg */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            Being a Disabled Woman in Luxembourg
          </h2>

          <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
            <img
              src="https://readdy.ai/api/search-image?query=disabled%20woman%20in%20Luxembourg%2C%20modern%20European%20city%2C%20accessibility%2C%20wheelchair%20user%2C%20professional%20woman%2C%20inclusive%20society%2C%20beautiful%20architecture%2C%20empowerment%2C%20dignity&width=1200&height=500&seq=luxembourg-disabled-1&orientation=landscape"
              alt="Disabled woman in Luxembourg"
              className="w-full h-80 object-cover object-top"
            />
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            Legal Framework
          </h3>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Luxembourg has ratified:</strong>
          </p>
          <ul className="space-y-2 mb-6">
            <li className="text-gray-700">• UN Convention on the Rights of Persons with Disabilities (CRPD) - 2011</li>
            <li className="text-gray-700">• Convention on the Elimination of All Forms of Discrimination Against Women (CEDAW) - 1989</li>
            <li className="text-gray-700">• European Convention on Human Rights</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>Domestic laws include:</strong>
          </p>
          <ul className="space-y-2 mb-6">
            <li className="text-gray-700">• Law on Equal Treatment (2006) - prohibits disability discrimination</li>
            <li className="text-gray-700">• Law on Accessibility (2001) - requires accessible public buildings</li>
            <li className="text-gray-700">• National Action Plan on Disability Rights</li>
            <li className="text-gray-700">• Strong social welfare system</li>
            <li className="text-gray-700">• Personal assistance services</li>
          </ul>

          <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg mb-8">
            <p className="text-gray-800 font-semibold mb-2">The Reality:</p>
            <p className="text-gray-700 mb-0">
              Luxembourg has strong laws and better enforcement than Turkey. Disabled women have more rights and resources. But discrimination and barriers still exist.
            </p>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            The Advantages in Luxembourg
          </h3>

          <div className="space-y-4 mb-8">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">✅ Better Physical Accessibility</p>
              <p className="text-gray-700 text-sm">Most public buildings, transportation, and sidewalks are accessible. New buildings must meet strict accessibility standards.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">✅ Employment Support</p>
              <p className="text-gray-700 text-sm">Job coaching, workplace accommodations, anti-discrimination laws with enforcement, supported employment programs.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">✅ Financial Support</p>
              <p className="text-gray-700 text-sm">Disability benefits, personal assistance funding, accessible housing subsidies, healthcare coverage.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">✅ Healthcare Access</p>
              <p className="text-gray-700 text-sm">Universal healthcare, accessible medical facilities, sign language interpreters, specialized services.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">✅ Education Inclusion</p>
              <p className="text-gray-700 text-sm">Inclusive education policies, support services in schools, accessible universities.</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            The Challenges That Remain in Luxembourg
          </h3>

          <div className="space-y-6 mb-8">
            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">1. Subtle Discrimination</h4>
              <p className="text-gray-700 mb-3">
                While overt discrimination is illegal and less common, disabled women still face subtle forms of discrimination.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Patronizing attitudes from healthcare providers</li>
                <li>• Lower expectations in education and employment</li>
                <li>• Exclusion from social events</li>
                <li>• Assumptions about capabilities</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">2. Employment Gap</h4>
              <p className="text-gray-700 mb-3">
                Despite support, disabled women still face employment challenges.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Employment rate for disabled women: 45% (vs. 65% for disabled men)</li>
                <li>• Wage gap: disabled women earn 20% less than non-disabled women</li>
                <li>• More likely to work part-time or in lower-paid positions</li>
                <li>• Career advancement is limited</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">3. Violence and Abuse</h4>
              <p className="text-gray-700 mb-3">
                Disabled women in Luxembourg still experience higher rates of violence.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• 2 times more likely to experience domestic violence</li>
                <li>• Sexual violence often goes unreported</li>
                <li>• Some shelters are not fully accessible</li>
                <li>• Disabled women may not be believed by authorities</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">4. Social Isolation</h4>
              <p className="text-gray-700 mb-3">
                Despite better accessibility, disabled women still face social barriers.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Difficulty forming romantic relationships</li>
                <li>• Exclusion from social activities</li>
                <li>• Loneliness and depression</li>
                <li>• Lack of representation in media and public life</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-3">5. Reproductive Rights Concerns</h4>
              <p className="text-gray-700 mb-3">
                While better than Turkey, issues remain.
              </p>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Pressure to not have children</li>
                <li>• Assumptions about parenting capabilities</li>
                <li>• Lack of accessible prenatal care</li>
                <li>• Custody challenges for disabled mothers</li>
              </ul>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            Voices from Luxembourg: Real Stories
          </h3>

          <div className="space-y-6 mb-8">
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Sophie, 31, wheelchair user, Luxembourg City:</p>
              <p className="text-gray-700 italic">
                "I have a good job, accessible apartment, and personal assistant. My life is much better than it would be in many countries. But I still face discrimination. Men don't see me as a potential partner. Doctors speak to my assistant instead of me. People assume I can't do things. The infrastructure is there, but the attitudes haven't fully changed."
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Laura, 38, visually impaired, Esch-sur-Alzette:</p>
              <p className="text-gray-700 italic">
                "I was sexually harassed at work. When I reported it, HR took it seriously and investigated. The man was fired. That would never happen in some countries. But it still happened. And I still had to fight to be believed. Being disabled and a woman means people question your version of events."
              </p>
            </div>

            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">Emma, 29, deaf, Differdange:</p>
              <p className="text-gray-700 italic">
                "I have sign language interpreters for work and medical appointments. That's amazing. But I still feel isolated. Most hearing people don't learn sign language. I can't participate fully in social events. Dating is hard because men see my deafness before they see me. I'm grateful for what Luxembourg provides, but I'm still lonely."
              </p>
            </div>
          </div>

          {/* Comparison */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            Comparing Turkey and Luxembourg: Key Differences
          </h2>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white shadow-lg rounded-lg overflow-hidden">
              <thead className="bg-purple-600 text-white">
                <tr>
                  <th className="p-4 text-left">Aspect</th>
                  <th className="p-4 text-left">Turkey</th>
                  <th className="p-4 text-left">Luxembourg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="p-4 font-semibold">Legal Framework</td>
                  <td className="p-4 text-sm">Laws exist but weak enforcement</td>
                  <td className="p-4 text-sm">Strong laws with better enforcement</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-semibold">Physical Accessibility</td>
                  <td className="p-4 text-sm">Poor (30% of buildings accessible)</td>
                  <td className="p-4 text-sm">Good (80%+ of buildings accessible)</td>
                </tr>
                <tr>
                  <td className="p-4 font-semibold">Employment Rate</td>
                  <td className="p-4 text-sm">15% of disabled women employed</td>
                  <td className="p-4 text-sm">45% of disabled women employed</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-semibold">Financial Support</td>
                  <td className="p-4 text-sm">Minimal disability benefits</td>
                  <td className="p-4 text-sm">Comprehensive benefits and services</td>
                </tr>
                <tr>
                  <td className="p-4 font-semibold">Healthcare Access</td>
                  <td className="p-4 text-sm">Limited accessibility, discrimination common</td>
                  <td className="p-4 text-sm">Universal healthcare, better accessibility</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-semibold">Education</td>
                  <td className="p-4 text-sm">40% of disabled girls never attend school</td>
                  <td className="p-4 text-sm">Inclusive education, high attendance</td>
                </tr>
                <tr>
                  <td className="p-4 font-semibold">Violence Rates</td>
                  <td className="p-4 text-sm">80% experience violence, low prosecution</td>
                  <td className="p-4 text-sm">Still elevated but better legal response</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-semibold">Social Stigma</td>
                  <td className="p-4 text-sm">Very high, disabled women often hidden</td>
                  <td className="p-4 text-sm">Lower but still present</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* What Needs to Change */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            What Needs to Change: Recommendations
          </h2>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            For Turkey
          </h3>

          <div className="space-y-4 mb-8">
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">1. Enforce Existing Laws</p>
              <p className="text-gray-700 text-sm">Penalties for non-compliance with accessibility and employment quotas. Regular inspections and monitoring.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">2. Improve Physical Accessibility</p>
              <p className="text-gray-700 text-sm">Massive investment in accessible infrastructure. Retrofit existing buildings. Accessible public transportation.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">3. Combat Violence</p>
              <p className="text-gray-700 text-sm">Accessible domestic violence shelters. Training for police and courts. Prosecution of abusers. Support services for survivors.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">4. Education Reform</p>
              <p className="text-gray-700 text-sm">Inclusive education policies. Accessible schools. Teacher training. Scholarships for disabled girls.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">5. Healthcare Improvements</p>
              <p className="text-gray-700 text-sm">Accessible medical facilities. Training for healthcare providers. Sign language interpreters. Reproductive healthcare access.</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">6. Change Social Attitudes</p>
              <p className="text-gray-700 text-sm">Public awareness campaigns. Representation in media. Disability rights education in schools.</p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">
            For Luxembourg
          </h3>

          <div className="space-y-4 mb-8">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">1. Address Subtle Discrimination</p>
              <p className="text-gray-700 text-sm">Training for healthcare providers, employers, educators. Awareness campaigns about unconscious bias.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">2. Close Employment Gap</p>
              <p className="text-gray-700 text-sm">Incentives for hiring disabled women. Career advancement programs. Equal pay enforcement.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">3. Improve Violence Response</p>
              <p className="text-gray-700 text-sm">Ensure all shelters are accessible. Training for police and courts. Support services specifically for disabled women.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">4. Combat Social Isolation</p>
              <p className="text-gray-700 text-sm">Social inclusion programs. Accessible social spaces. Representation in media and public life.</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">5. Support Reproductive Rights</p>
              <p className="text-gray-700 text-sm">Accessible prenatal care. Support for disabled parents. Combat stereotypes about disabled mothers.</p>
            </div>
          </div>

          {/* Resilience and Strength */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            The Strength of Disabled Women
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Despite all the challenges, disabled women are not victims. They are survivors. They are fighters. They are leaders.
          </p>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-xl mb-8">
            <p className="text-gray-800 leading-relaxed mb-4">
              <strong>Disabled women are:</strong>
            </p>
            <ul className="space-y-3">
              <li className="text-gray-700">• Starting businesses and creating employment for others</li>
              <li className="text-gray-700">• Leading disability rights movements</li>
              <li className="text-gray-700">• Advocating for policy changes</li>
              <li className="text-gray-700">• Supporting other disabled women</li>
              <li className="text-gray-700">• Raising children and building families</li>
              <li className="text-gray-700">• Creating art, writing books, making films</li>
              <li className="text-gray-700">• Challenging stereotypes every day</li>
              <li className="text-gray-700">• Refusing to be invisible</li>
              <li className="text-gray-700">• Demanding their rights</li>
              <li className="text-gray-700">• Living full, rich, meaningful lives</li>
            </ul>
          </div>

          {/* Conclusion */}
          <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">
            Conclusion: Solidarity Across Borders
          </h2>

          <p className="text-gray-700 leading-relaxed mb-6">
            Being a disabled woman in Turkey is vastly different from being a disabled woman in Luxembourg. The legal frameworks, resources, and social attitudes are worlds apart.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            But disabled women in both countries face discrimination. They face barriers. They face violence. They face stigma. The degree may differ, but the struggle is shared.
          </p>

          <p className="text-gray-700 leading-relaxed mb-6">
            <strong>What unites disabled women across borders is:</strong>
          </p>

          <ul className="space-y-3 mb-8">
            <li className="text-gray-700">• The experience of double discrimination</li>
            <li className="text-gray-700">• The fight for basic rights and dignity</li>
            <li className="text-gray-700">• The resilience in the face of barriers</li>
            <li className="text-gray-700">• The determination to live full lives</li>
            <li className="text-gray-700">• The solidarity with other disabled women</li>
            <li className="text-gray-700">• The refusal to be invisible or silent</li>
          </ul>

          <div className="bg-purple-600 text-white p-8 rounded-xl mb-8">
            <p className="text-2xl font-bold mb-4">
              To disabled women everywhere:
            </p>
            <p className="text-lg mb-4">
              You are not broken. You are not less than. You are not a burden.
            </p>
            <p className="text-lg mb-4">
              You are whole. You are valuable. You are powerful.
            </p>
            <p className="text-lg mb-4">
              Your disability is part of you, but it does not define you.
            </p>
            <p className="text-lg mb-0">
              You deserve rights. You deserve respect. You deserve to live freely and fully.
            </p>
          </div>

          <p className="text-xl text-gray-800 font-semibold mt-8">
            Nothing about us without us. ✊💜
          </p>
        </div>

        {/* Call to Action */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-4">Support Disabled Women's Rights</h3>
          <p className="text-lg mb-6 opacity-90">
            Join our movement to fight for disability rights and gender equality.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/volunteer"
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              Get Involved
            </Link>
            <Link
              to="/donate"
              className="bg-blue-800 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-900 transition-colors whitespace-nowrap"
            >
              Donate
            </Link>
            <Link
              to="/resources"
              className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap"
            >
              Find Resources
            </Link>
          </div>
        </div>

        {/* Share */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-600 mb-4">Share this article:</p>
          <div className="flex gap-4">
            <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap">
              <i className="ri-facebook-fill mr-2"></i>
              Facebook
            </button>
            <button className="bg-sky-500 text-white px-6 py-2 rounded-lg hover:bg-sky-600 transition-colors whitespace-nowrap">
              <i className="ri-twitter-fill mr-2"></i>
              Twitter
            </button>
            <button className="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition-colors whitespace-nowrap">
              <i className="ri-instagram-fill mr-2"></i>
              Instagram
            </button>
          </div>
        </div>
      </article>
    </div>
  );
}
